import type { FunctionReference, FunctionReturnType } from "convex/server";
import { ConvexClient as ConvexClientImpl } from "convex/browser";
import { Context, Effect, Layer, Stream } from "effect";

export interface ConvexClientService {
	query<Query extends FunctionReference<"query">>(
		query: Query,
		args: Query["_args"],
	): Effect.Effect<FunctionReturnType<Query>>;

	mutation<Mutation extends FunctionReference<"mutation">>(
		mutation: Mutation,
		args: Mutation["_args"],
	): Effect.Effect<FunctionReturnType<Mutation>>;

	action<Action extends FunctionReference<"action">>(
		action: Action,
		args: Action["_args"],
	): Effect.Effect<FunctionReturnType<Action>>;

	subscribe<Query extends FunctionReference<"query">>(
		query: Query,
		args: Query["_args"],
	): Stream.Stream<FunctionReturnType<Query>>;
}

export class ConvexClient extends Context.Tag("@confect/ConvexClient")<
	ConvexClient,
	ConvexClientService
>() {}

export const ConvexClientLayer = (
	url: string,
): Layer.Layer<ConvexClient> => {
	let client: ConvexClientImpl | null = null;

	const getClient = () => {
		if (url.length === 0) {
			throw new Error("NEXT_PUBLIC_CONVEX_URL is required for Convex access");
		}

		if (client === null) {
			client = new ConvexClientImpl(url);
		}

		return client;
	};

	const service: ConvexClientService = {
		query: <Query extends FunctionReference<"query">>(
			query: Query,
			args: Query["_args"],
		): Effect.Effect<FunctionReturnType<Query>> =>
			Effect.promise(() => getClient().query(query, args)),

		mutation: <Mutation extends FunctionReference<"mutation">>(
			mutation: Mutation,
			args: Mutation["_args"],
		): Effect.Effect<FunctionReturnType<Mutation>> =>
			Effect.promise(() => getClient().mutation(mutation, args)),

		action: <Action extends FunctionReference<"action">>(
			action: Action,
			args: Action["_args"],
		): Effect.Effect<FunctionReturnType<Action>> =>
			Effect.promise(() => getClient().action(action, args)),

		subscribe: <Query extends FunctionReference<"query">>(
			query: Query,
			args: Query["_args"],
		): Stream.Stream<FunctionReturnType<Query>> =>
			Stream.unwrap(
				Effect.sync(() => {
					const activeClient = getClient();
					return Stream.async<FunctionReturnType<Query>>((emit) => {
						const unsubscribe = activeClient.onUpdate(query, args, (result) => {
							emit.single(result);
						});
						return Effect.sync(() => {
							unsubscribe();
						});
					});
				}),
			),
	};

	return Layer.succeed(ConvexClient, service);
};
